/**
 * Subtle fade-in on scroll.
 *
 * Targets the block-level children of each section — headings, copy, tables,
 * rosters, images — but never the decorative rivers, which are positioned and
 * would jump. The hero is left alone: it is above the fold, so fading it in
 * would just look like a slow page load.
 *
 * The starting (hidden) state lives behind `html.js`, so with JS off nothing
 * is ever hidden. Reduced motion skips the whole thing.
 */

const SELECTOR = '.section > *:not(.river), .wp-block-donut-table, .is-style-roster > *';

export default function reveal(): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const els = Array.from(document.querySelectorAll<HTMLElement>(SELECTOR))
    .filter((el) => !el.closest('.page-hero'));
  if (!els.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        (entry.target as HTMLElement).classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  els.forEach((el, i) => {
    el.classList.add('reveal');
    // a short stagger between siblings so a section arrives as a group
    el.style.setProperty('--reveal-delay', `${Math.min(i % 6, 5) * 60}ms`);
    observer.observe(el);
  });
}
